/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJobTasks, Entity} from '@ion/core/src/types/entities';
import {DeletePrompt} from '@ion/dls/src/editors/DeletePrompt';
import {RemoveTaskValueType} from './helpers';

export interface DependentTaskDeletePromptProps {
  setRemoveTaskValue: Function;
  removeTaskValue: RemoveTaskValueType;
  removeTask: (index: number, jobTasks: DownloadJobTasks) => void;
};

// Entity title for 'Task'
let entityTitle: string;
const getEntityTitle = (): string => {
  if (!entityTitle) {
    entityTitle = translate(`common:entityLabels.${Entity.task}`);
  }
  return entityTitle;
};

/**
 * Defines the `DependentTaskDeletePrompt` component. This component displays a dialog
 * to the user to confirm their decision before deleting a task.
 * 
 * ```typescript
 * 
 *   import DependentTaskDeletePrompt from './DependentTaskDeletePrompt';
 * 
 *   <DependentTaskDeletePrompt .../>
 * 
 * ```      
 */
export const DependentTaskDeletePrompt = ({
  removeTaskValue,
  removeTask,
  setRemoveTaskValue
}: DependentTaskDeletePromptProps) => {
  const {index, tasks} = removeTaskValue || {};
  const task           = tasks?.[index]?.task;
  const dependency     = task.metaData?.dependency?.index;
  const hasChildren    = task.children?.length > 0;
  const path           = 'jobcreate:dependentTask.deleteDialog';

  // Generates a custom delete message for the selected Task
  const getDeleteMessage = useCallback(function() {
    const message = translate(`${path}.message`);
    return (!isUndefined(dependency) || hasChildren)
      ? `${message} ${translate(`${path}.dependentMessage`)}`
      : message;
  }, [dependency, hasChildren]);

  const onConfirm = useCallback(function(): void {
    removeTask(index, tasks);
  }, [index, tasks, removeTaskValue]);

  const onCancel = useCallback(function(): void {
    setRemoveTaskValue(undefined);
  }, []);

  return (
    <DeletePrompt show
      entity={getEntityTitle()}
      items={tasks}
      overrideMessage={getDeleteMessage}
      onConfirm={onConfirm}
      onCancel={onCancel} />
  );
};

export default DependentTaskDeletePrompt;
